import * as React from "react";
import { cn } from "@/lib/utils";
import { Reveal } from "./motion";

type SectionProps = React.HTMLAttributes<HTMLElement> & {
  /** Constrains the inner content width. Defaults to 7xl. */
  width?: "5xl" | "6xl" | "7xl";
  /** Adds vertical padding. Defaults to true. */
  padded?: boolean;
};

const widthMap = {
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
} as const;

/**
 * Semantic section wrapper with a centered, padded container. Use `id` for
 * in-page anchors.
 */
export function Section({
  children,
  className,
  width = "7xl",
  padded = true,
  ...props
}: SectionProps) {
  return (
    <section className={cn(padded && "py-20 lg:py-28", className)} {...props}>
      <div className={cn("mx-auto px-4 lg:px-8", widthMap[width])}>{children}</div>
    </section>
  );
}

export function Eyebrow({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-block text-sm font-semibold uppercase tracking-[0.08em] text-primary dark:text-brand-accent",
        className,
      )}
    >
      {children}
    </span>
  );
}

type SectionHeadingProps = {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  align?: "left" | "center";
  className?: string;
  /** Animate into view on scroll. Defaults to true. */
  animate?: boolean;
  /**
   * Visual scale of the title. `lg` (default) is the marquee size used on the
   * homepage. `sm` renders at h3 sizing for inner-page sections that sit below
   * a page h1 — the tag stays an `<h2>` so the document outline is correct.
   */
  size?: "lg" | "sm";
};

const TITLE_SIZE = {
  lg: "text-3xl font-extrabold md:text-4xl md:leading-[1.1]",
  sm: "text-2xl font-bold md:text-3xl md:leading-[1.15]",
} as const;

export function SectionHeading({
  eyebrow,
  title,
  description,
  align = "center",
  className,
  animate = true,
  size = "lg",
}: SectionHeadingProps) {
  const isCenter = align === "center";
  const content = (
    <div className={cn(isCenter ? "mx-auto max-w-2xl text-center" : "text-left", className)}>
      {eyebrow ? (
        typeof eyebrow === "string" ? (
          <Eyebrow className="mb-3">{eyebrow}</Eyebrow>
        ) : (
          // Custom eyebrow node controls its own styling (e.g. icon + label).
          <div className="mb-3">{eyebrow}</div>
        )
      ) : null}
      <h2
        className={cn(
          "tracking-tight text-foreground",
          // Centered marquee headlines are short — balance them. Left-aligned
          // inner-page headings often pair a bold lead with a longer muted run,
          // where text-balance rags badly across many lines; text-pretty wraps
          // naturally and only guards against orphans.
          isCenter ? "text-balance" : "text-pretty",
          TITLE_SIZE[size],
          // Constrain the headline so it wraps nicely; the subtitle stays full
          // width when left-aligned.
          !isCenter && "max-w-3xl",
        )}
      >
        {title}
      </h2>
      {description ? (
        <p
          className={cn(
            "mt-4 leading-relaxed text-muted-foreground sm:text-lg",
            isCenter && "text-balance",
          )}
        >
          {description}
        </p>
      ) : null}
    </div>
  );

  if (!animate) return content;
  return <Reveal>{content}</Reveal>;
}
